import { useMemo, useState } from "react";
import { Pencil, Search, Trash2 } from "lucide-react";
import type { Subscription } from "../types/subscription";
import { formatMoney, monthlyEquivalent } from "../lib/billing";
import { cn } from "../lib/cn";
import { useUserSettings } from "../lib/settings-context";

type SortKey = "name" | "monthly" | "renewal" | "category";

type Props = {
  subscriptions: Subscription[];
  onEdit: (s: Subscription) => void;
  onDelete: (s: Subscription) => void;
  onToggleActive: (s: Subscription, active: boolean) => void;
};

const cycleLabel: Record<string, string> = {
  monthly: "Monthly",
  yearly: "Yearly",
  custom_days: "Custom",
};

export function SubscriptionTable({
  subscriptions,
  onEdit,
  onDelete,
  onToggleActive,
}: Props) {
  const { settings } = useUserSettings();
  const nfLocale = settings.numberLocale.trim() || undefined;
  const [q, setQ] = useState("");
  const [sort, setSort] = useState<{ key: SortKey; dir: "asc" | "desc" }>({
    key: "renewal",
    dir: "asc",
  });

  const filtered = useMemo(() => {
    let rows = subscriptions;
    if (!settings.subscriptionListShowInactive) {
      rows = rows.filter((s) => s.active === 1);
    }
    const needle = q.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter(
      (s) =>
        s.name.toLowerCase().includes(needle) ||
        s.category.toLowerCase().includes(needle),
    );
  }, [subscriptions, q, settings.subscriptionListShowInactive]);

  const sorted = useMemo(() => {
    const rows = [...filtered];
    const mult = sort.dir === "asc" ? 1 : -1;
    rows.sort((a, b) => {
      switch (sort.key) {
        case "name":
          return mult * a.name.localeCompare(b.name);
        case "category":
          return mult * a.category.localeCompare(b.category);
        case "monthly":
          return mult * (monthlyEquivalent(a) - monthlyEquivalent(b));
        case "renewal":
          return mult * a.next_renewal_date.localeCompare(b.next_renewal_date);
        default:
          return 0;
      }
    });
    return rows;
  }, [filtered, sort]);

  function toggleSort(key: SortKey) {
    setSort((prev) =>
      prev.key === key
        ? { key, dir: prev.dir === "asc" ? "desc" : "asc" }
        : { key, dir: "asc" },
    );
  }

  function requestDelete(s: Subscription) {
    const run = () => onDelete(s);
    if (!settings.confirmBeforeDelete) {
      run();
      return;
    }
    if (confirm(`Delete “${s.name}”? This cannot be undone.`)) {
      run();
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="relative max-w-md flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-muted" />
          <input
            type="search"
            enterKeyHint="search"
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
            placeholder="Search name or category…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="min-h-11 w-full rounded-xl border border-border bg-surface-1 py-2.5 pl-9 pr-3 text-base text-text placeholder:text-text-muted focus:border-accent focus:ring-1 focus:ring-accent/40 sm:text-sm"
          />
        </div>
      </div>

      {/* Card list: readable on narrow viewports without horizontal panning */}
      <ul className="space-y-3 md:hidden" aria-label="Subscriptions">
        {sorted.length === 0 ? (
          <li className="rounded-2xl border border-border bg-surface-1/80 px-4 py-10 text-center text-sm text-text-muted">
            No subscriptions match your search.
          </li>
        ) : (
          sorted.map((s) => (
            <li
              key={s.id}
              className={cn(
                "rounded-2xl border border-border bg-surface-1/80 p-4 backdrop-blur-sm",
                s.active === 0 && "opacity-70",
              )}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-text">{s.name}</p>
                  <p className="mt-0.5 text-sm text-text-muted">{s.category}</p>
                </div>
                <label className="flex shrink-0 items-center gap-2 touch-manipulation text-sm text-text-muted">
                  <span className="sr-only">Active</span>
                  <input
                    type="checkbox"
                    className="h-5 w-5 rounded border-border bg-surface-0 text-accent"
                    checked={s.active === 1}
                    onChange={(e) => onToggleActive(s, e.target.checked)}
                  />
                </label>
              </div>
              <dl className="mt-3 grid grid-cols-2 gap-x-3 gap-y-2 text-sm">
                <div>
                  <dt className="text-xs uppercase tracking-wide text-text-muted">Cycle</dt>
                  <dd className="mt-0.5 text-text">
                    {cycleLabel[s.billing_cycle] ?? s.billing_cycle}
                    {s.billing_cycle === "custom_days" && s.custom_period_days
                      ? ` (${s.custom_period_days}d)`
                      : ""}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs uppercase tracking-wide text-text-muted">Monthly eq.</dt>
                  <dd className="mt-0.5 tabular-nums text-text">
                    {formatMoney(monthlyEquivalent(s), s.currency, nfLocale)}
                  </dd>
                </div>
                <div className="col-span-2">
                  <dt className="text-xs uppercase tracking-wide text-text-muted">Renewal</dt>
                  <dd className="mt-0.5 tabular-nums text-text">{s.next_renewal_date}</dd>
                </div>
              </dl>
              <div className="mt-4 flex gap-2 border-t border-border/60 pt-3">
                <button
                  type="button"
                  className="inline-flex min-h-11 min-w-11 flex-1 touch-manipulation items-center justify-center gap-2 rounded-xl border border-border bg-surface-0/80 text-sm font-medium text-text hover:bg-surface-2"
                  onClick={() => onEdit(s)}
                >
                  <Pencil className="h-4 w-4 shrink-0" aria-hidden />
                  Edit
                </button>
                <button
                  type="button"
                  className="inline-flex min-h-11 min-w-11 flex-1 touch-manipulation items-center justify-center gap-2 rounded-xl border border-border bg-surface-0/80 text-sm font-medium text-danger hover:bg-danger/10"
                  onClick={() => requestDelete(s)}
                >
                  <Trash2 className="h-4 w-4 shrink-0" aria-hidden />
                  Delete
                </button>
              </div>
            </li>
          ))
        )}
      </ul>

      <div className="hidden overflow-x-auto rounded-2xl border border-border bg-surface-1/80 md:block">
        <table className="w-full min-w-[720px] text-left text-sm">
          <thead>
            <tr className="border-b border-border text-xs uppercase tracking-wide text-text-muted">
              <th className="px-4 py-3 font-medium">
                <button
                  type="button"
                  className="hover:text-text"
                  onClick={() => toggleSort("name")}
                >
                  Name {sort.key === "name" ? (sort.dir === "asc" ? "↑" : "↓") : ""}
                </button>
              </th>
              <th className="px-4 py-3 font-medium">Cycle</th>
              <th className="px-4 py-3 font-medium">
                <button
                  type="button"
                  className="hover:text-text"
                  onClick={() => toggleSort("monthly")}
                >
                  Monthly eq.{" "}
                  {sort.key === "monthly" ? (sort.dir === "asc" ? "↑" : "↓") : ""}
                </button>
              </th>
              <th className="px-4 py-3 font-medium">
                <button
                  type="button"
                  className="hover:text-text"
                  onClick={() => toggleSort("renewal")}
                >
                  Renewal{" "}
                  {sort.key === "renewal" ? (sort.dir === "asc" ? "↑" : "↓") : ""}
                </button>
              </th>
              <th className="px-4 py-3 font-medium">
                <button
                  type="button"
                  className="hover:text-text"
                  onClick={() => toggleSort("category")}
                >
                  Category{" "}
                  {sort.key === "category" ? (sort.dir === "asc" ? "↑" : "↓") : ""}
                </button>
              </th>
              <th className="px-4 py-3 font-medium">Active</th>
              <th className="px-4 py-3 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {sorted.length === 0 ? (
              <tr>
                <td
                  colSpan={7}
                  className="px-4 py-12 text-center text-text-muted"
                >
                  No subscriptions match your search.
                </td>
              </tr>
            ) : (
              sorted.map((s) => (
                <tr
                  key={s.id}
                  className={cn(
                    "border-b border-border/60 last:border-0",
                    s.active === 0 && "opacity-60",
                  )}
                >
                  <td className="px-4 py-3 font-medium text-text">{s.name}</td>
                  <td className="px-4 py-3 text-text-muted">
                    {cycleLabel[s.billing_cycle] ?? s.billing_cycle}
                    {s.billing_cycle === "custom_days" && s.custom_period_days
                      ? ` (${s.custom_period_days}d)`
                      : ""}
                  </td>
                  <td className="px-4 py-3 tabular-nums text-text">
                    {formatMoney(monthlyEquivalent(s), s.currency, nfLocale)}
                  </td>
                  <td className="px-4 py-3 tabular-nums text-text-muted">
                    {s.next_renewal_date}
                  </td>
                  <td className="px-4 py-3 text-text-muted">{s.category}</td>
                  <td className="px-4 py-3">
                    <label className="inline-flex cursor-pointer items-center gap-2">
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded border-border bg-surface-0 text-accent"
                        checked={s.active === 1}
                        onChange={(e) => onToggleActive(s, e.target.checked)}
                      />
                    </label>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      type="button"
                      className="inline-flex rounded-lg p-2 text-text-muted hover:bg-surface-2 hover:text-text"
                      aria-label={`Edit ${s.name}`}
                      onClick={() => onEdit(s)}
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      className="inline-flex rounded-lg p-2 text-text-muted hover:bg-surface-2 hover:text-danger"
                      aria-label={`Delete ${s.name}`}
                      onClick={() => requestDelete(s)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
